<!--
 * @Description: 
 * @Author: luckymiaow
 * @Date: 2022-05-11 14:49:40
 * @LastEditors: luckymiaow
-->

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, unref, useAttrs, watchEffect } from 'vue';
import '@wangeditor/editor/dist/css/style.css';
import type { IDomEditor, IToolbarConfig, Toolbar } from '@wangeditor/editor';
import { createEditor, createToolbar } from '@wangeditor/editor';
import type { EditorProps } from '../types';
import useForm, { readOnlySlot } from '../../form/src/useFormConfig';
import useEditor from './useEditor';

interface Props extends EditorProps {
  value?: string;
  toolbarConfig?: Partial<IToolbarConfig>;
  mode?: 'default' | 'simple';
}

const props = withDefaults(defineProps<Props>(), {
  mode: 'default',
});

const emits = defineEmits<{ (event: 'update:value', value: string): void }>();

const toolbarContainer = ref<Element>();
const editorContainer = ref<Element>();
const editor = ref<IDomEditor>();
const toolbar = ref<Toolbar>();

const { getBindValue } = useForm<EditorProps>({ props, componentName: 'Editor', attrs: useAttrs() });

const { editorConfig, toolbarConfig } = useEditor({ emits, getBindValue: getBindValue.value });

const bordered = computed(() => {
  if (unref(getBindValue).bordered === undefined) return true;
  return unref(getBindValue).bordered;
});

watchEffect(() => {
  editor.value?.setHtml(props.value || '');
});

function init() {
  // 创建编辑器
  editor.value = createEditor({
    selector: editorContainer.value,
    config: editorConfig,
    html: props.value,
  });

  // 创建工具栏
  toolbar.value = createToolbar({
    editor: editor.value,
    selector: toolbarContainer.value as Element,
    config: toolbarConfig,
    mode: props.mode,
  });
}

onMounted(async () => {
  await nextTick();
  init();
});

defineExpose({ editor, toolbar });
</script>

<script lang="ts">
export default {
  inheritAttrs: false,
};
</script>

<template>
  <div
    :class="{ bordered, [getBindValue?.readOnlyClassName!]: getBindValue?.readOnlyClassName }"
    :style="getBindValue?.readOnlyStyle!"
  >
    <slot v-if="getBindValue.readOnly" :name="readOnlySlot" :text="value"><div v-html="value"></div> </slot>
  </div>
  <div v-show="!getBindValue.readOnly" :class="{ bordered }">
    <div ref="toolbarContainer" class="tool"></div>
    <div ref="editorContainer" class="editor" :style="$attrs.style!"></div>
  </div>
</template>

<style lang="less" setup>
.bordered {
  border: 1px solid #ccc;
  .tool {
    border-bottom: 1px solid #ccc;
  }
}
</style>
